import type { ChartData } from 'chart.js'
import type {
  AiOperationsAnalyticsResponse,
  OperatorKtuRow,
  OperatorOverviewRow,
  ProjectRow,
} from '@/types/api'
import {
  PROJECT_RESOLUTION_TIME_COLOR,
  PROJECT_RESPONSE_TIME_COLOR,
  channelSourceKey,
  channelSourceLabel,
  currentColor,
  operatorColors,
  previousColor,
  shortLabel,
  sourceColors,
  type ChannelStatsRow,
  type ProductStatsRow,
} from '@/pages/analytics/analytics-new-utils'

export type AiReasonChartRow = {
  key: string
  label: string
  current: number
  previous: number
  pctTransfers: number
  pctTotal: number
}

export function buildOperatorChartData(
  operators: OperatorOverviewRow[],
  previousByOperator: Map<string, number>,
): ChartData<'bar'> {
  return {
    labels: operators.map((op) => shortLabel(op.operator_name || '—', 10)),
    datasets: [
      { label: 'Текущий период', data: operators.map((op) => Number(op.closed_tickets) || 0), backgroundColor: operators.map((_, index) => operatorColors[index % operatorColors.length]), borderRadius: 8, maxBarThickness: 14 },
      { label: 'Предыдущий период', data: operators.map((op) => previousByOperator.get(op.operator_id || op.operator_name || '') || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 14 },
    ],
  }
}

export function buildProductChartData(
  products: ProductStatsRow[],
  previousByProduct: Map<string, number>,
  projects: ProjectRow[],
): ChartData<'bar'> {
  return {
    labels: products.map((row) => shortLabel(projects.find((p) => p.id === row.project_id)?.name || row.project_name || '—', 10)),
    datasets: [
      { label: 'Текущий период', data: products.map((row) => Number(row.total) || 0), backgroundColor: currentColor, borderRadius: 8, maxBarThickness: 14 },
      { label: 'Предыдущий период', data: products.map((row) => previousByProduct.get(row.project_id || '') || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 14 },
    ],
  }
}

export function buildSourceChartData(
  rows: ChannelStatsRow[],
  previousBySource: Map<string, number>,
): ChartData<'bar'> {
  return {
    labels: rows.map((row) => channelSourceLabel(row.channel_type)),
    datasets: [
      { label: 'Текущий период', data: rows.map((row) => Number(row.total) || 0), backgroundColor: rows.map((_, index) => sourceColors[index % sourceColors.length]), borderRadius: 8, maxBarThickness: 24 },
      { label: 'Предыдущий период', data: rows.map((row) => previousBySource.get(channelSourceKey(row)) || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 24 },
    ],
  }
}

export function buildOperatorKtuChartData(
  operators: OperatorKtuRow[],
  previousByOperator: Map<string, number>,
): ChartData<'bar'> {
  return {
    labels: operators.map((op) => shortLabel(op.operator_name || op.operator_email || '—', 10)),
    datasets: [
      { label: 'Текущий период', data: operators.map((op) => Number(op.ktu) || 0), backgroundColor: operators.map((_, index) => operatorColors[index % operatorColors.length]), borderRadius: 8, maxBarThickness: 14 },
      { label: 'Предыдущий период', data: operators.map((op) => previousByOperator.get(op.operator_id || op.operator_name || '') || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 14 },
    ],
  }
}

export function buildProjectTimeChartData(
  rows: ProductStatsRow[],
  previousResponseByProject: Map<string, number>,
  previousCloseByProject: Map<string, number>,
  projects: ProjectRow[],
): ChartData<'bar'> {
  return {
    labels: rows.map((row) => shortLabel(projects.find((p) => p.id === row.project_id)?.name || row.project_name || '—', 10)),
    datasets: [
      { label: 'Ответ — текущий период', data: rows.map((row) => Number(row.avg_first_response_minutes) || 0), backgroundColor: PROJECT_RESPONSE_TIME_COLOR, borderRadius: 8, maxBarThickness: 10 },
      { label: 'Ответ — предыдущий период', data: rows.map((row) => previousResponseByProject.get(row.project_id || '') || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 10 },
      { label: 'Решение — текущий период', data: rows.map((row) => Number(row.avg_close_minutes) || 0), backgroundColor: PROJECT_RESOLUTION_TIME_COLOR, borderRadius: 8, maxBarThickness: 10 },
      { label: 'Решение — предыдущий период', data: rows.map((row) => previousCloseByProject.get(row.project_id || '') || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 10 },
    ],
  }
}

export function buildAiReasonChartData(rows: AiReasonChartRow[]): ChartData<'bar'> {
  return {
    labels: rows.map((row) => row.label),
    datasets: [
      { label: 'Текущий период', data: rows.map((row) => row.current), backgroundColor: currentColor, borderRadius: 8, maxBarThickness: 24 },
      { label: 'Предыдущий период', data: rows.map((row) => row.previous), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 24 },
    ],
  }
}

export function buildAiProjectChartData(
  rows: NonNullable<AiOperationsAnalyticsResponse['by_project']>,
  previousByProject: Map<string, NonNullable<AiOperationsAnalyticsResponse['by_project']>[number]>,
): ChartData<'bar'> {
  return {
    labels: rows.map((row) => shortLabel(row.project_name || row.project_id, 14)),
    datasets: [
      { label: 'База без аварий', data: rows.map((row) => Number(row.non_emergency_tickets) || 0), backgroundColor: currentColor, borderRadius: 8, maxBarThickness: 24 },
      { label: 'Предыдущий период', data: rows.map((row) => Number(previousByProject.get(row.project_id)?.non_emergency_tickets) || 0), backgroundColor: previousColor, borderRadius: 8, maxBarThickness: 24 },
    ],
  }
}
